import { memo, useState, useMemo } from 'react';
import { Handle, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { PlayFilled } from '@fluentui/react-icons';
import { useConnectorInfo, useConnectorContext } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import { useHandleLayout } from './useHandlePositions';
import { useNodeConnectionStatus } from '@/hooks/useNodeConnectionStatus';
import { useNodeValidationErrors } from '@/hooks/useProactiveValidation';
import { ExecutionOverlay } from '@/components/shared';
import { ValidationErrorBadge } from './ValidationErrorBadge';
import {
  FloatingNode,
  NodeHoverToolbar,
  useDetailLevel,
  type NodeStatus,
} from './FloatingNode';
import { ParameterPreview } from './ParameterPreview';
import { getBuiltInManifest } from '@/data/manifests';
import type { NodeExecutionStatus } from '@/types/monitoring';
import { isScopeBranch } from '../helpers/node';

export type FloatingActionNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  connectionName?: string;
  /** Key parameters to preview on the node (shown in detailed zoom level) */
  parameters?: Record<string, string | number | boolean | null | undefined>;
  // Monitoring mode props
  executionStatus?: NodeExecutionStatus;
  executionDuration?: number;
  // Parallel branch metadata
  isMergePoint?: boolean;
  // Scope metadata (set by useWorkflowGraph for nodes inside ForEach/If/Switch)
  scopeParentId?: string;
  branchType?: string;
  // Toolbar action callbacks (injected by WorkflowDesigner)
  onOpen?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onEditWithAI?: () => void;
};

export type FloatingActionNodeType = Node<FloatingActionNodeData, 'floatingAction'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  connectorIcon: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
  },
});

/**
 * Floating-style Action Node with minimal visual footprint.
 * Shows icon + label by default, details on hover via popover.
 */
export const FloatingActionNode = memo(
  ({ data, selected, id }: NodeProps<FloatingActionNodeType>) => {
    const styles = useStyles();
    const { isLoading } = useConnectorContext();
    // Look up connector display info first by connectionName (when a connection
    // exists), then fall back to the action name. useWorkflowConnectors keys the
    // map by both, so connection-less actions still resolve their connector icon
    // / display name from the manifest — no display fields are persisted on the
    // action itself.
    const connectorInfoByConnection = useConnectorInfo(data.connectionName);
    const connectorInfoByAction = useConnectorInfo(actionNameKey(data.actionName));
    const connectorInfo = connectorInfoByConnection ?? connectorInfoByAction;
    const [imageError, setImageError] = useState(false);
    const handles = useHandleLayout();
    const detailLevel = useDetailLevel();

    // Get connection validation status
    const { validationResult } = useNodeConnectionStatus({
      nodeId: id,
      operationType: data.actionType,
      connectionReferenceKey: data.connectionName,
    });

    // Get proactive validation errors for this node
    const { errors: validationErrors } = useNodeValidationErrors(data.actionName);

    // Show loading state when we have a connection but no info yet
    const showLoading = isLoading && data.connectionName && !connectorInfo;

    // Get built-in manifest info as fallback for operations without connections
    const builtInManifest = useMemo(() => {
      if (connectorInfo) return null;
      return getBuiltInManifest(data.actionType);
    }, [connectorInfo, data.actionType]);

    // Use connector info for display, falling back to the built-in manifest
    // (e.g. for control-flow nodes like If / Foreach), then to the action type.
    const displayName = connectorInfo?.displayName ?? data.actionType;
    const iconUri = connectorInfo?.iconUri ?? (builtInManifest?.properties?.iconUri as string | undefined);
    const brandColor = connectorInfo?.brandColor ?? (builtInManifest?.properties?.brandColor as string | undefined);
    const showIcon = iconUri && !imageError;

    // Map execution status to FloatingNode status
    const getNodeStatus = (): NodeStatus => {
      if (!data.executionStatus) {
        // Check validation status when not in monitoring mode
        if (validationResult) {
          if (validationResult.status === 'mock-active') return 'mock';
          if (!validationResult.isValid) {
            return validationResult.status === 'warning' ? 'warning' : 'error';
          }
        }
        return 'default';
      }

      switch (data.executionStatus) {
        case 'running':
          return 'running';
        case 'succeeded':
          return 'success';
        case 'failed':
          return 'failed';
        case 'skipped':
          return 'skipped';
        case 'pending':
          return 'default';
        default:
          return 'default';
      }
    };

    // Build status message for error/mock badge tooltip
    const statusMessage =
      validationResult?.status === 'mock-active'
        ? 'Using test data'
        : validationResult && !validationResult.isValid
          ? validationResult.error?.message ?? 'Connection not configured'
          : undefined;

    // Render the icon
    const renderIcon = () => {
      if (showLoading) {
        return <Spinner size="extra-tiny" aria-label="Loading connector info" />;
      }

      if (showIcon) {
        return (
          <img
            src={iconUri}
            alt=""
            className={styles.connectorIcon}
            aria-hidden="true"
            onError={() => setImageError(true)}
          />
        );
      }

      return <PlayFilled aria-hidden="true" />;
    };

    const isMonitoringMode = data.executionStatus !== undefined;

    return (
      <div className={styles.wrapper}>
        <NodeHoverToolbar
          onConfigure={data.onOpen}
          onDelete={data.onDelete}
          onDuplicate={isScopeBranch(data.branchType) ? undefined : data.onDuplicate}
          onEditWithAI={data.onEditWithAI}
          showToolbar={!isMonitoringMode}
          disabled={selected}
        >
          <FloatingNode
            icon={renderIcon()}
            label={data.label}
            subLabel={showLoading ? 'Loading...' : displayName}
            detailLevel={detailLevel}
            selected={selected}
            status={getNodeStatus()}
            statusMessage={statusMessage}
            accentColor="brand"
            brandColorOverride={brandColor}
            ariaLabel={`${data.label} ${displayName} action node`}
          >
            <Handle
              type="target"
              position={handles.input.position}
              id="input"
              isConnectable={true}
              style={handles.input.style}
            />
            <Handle
              type="source"
              position={handles.output.position}
              id="output"
              isConnectable={!(data.scopeParentId && data.branchType === 'loop')}
              style={handles.output.style}
            />
          </FloatingNode>
        </NodeHoverToolbar>

        {/* Show parameter preview in detailed mode (design mode only) */}
        {!isMonitoringMode && detailLevel === 'detailed' && data.parameters && (
          <ParameterPreview parameters={data.parameters} maxItems={2} size="small" />
        )}

        {/* Show execution overlay in monitoring mode */}
        {isMonitoringMode && (
          <ExecutionOverlay
            status={data.executionStatus!}
            duration={data.executionDuration}
            position="top-right"
          />
        )}

        {/* Show validation error badge (only in design mode) */}
        {!isMonitoringMode && validationErrors.length > 0 && (
          <ValidationErrorBadge
            nodeName={data.actionName}
            errors={validationErrors}
            position="top-left"
            size="small"
          />
        )}
      </div>
    );
  }
);

FloatingActionNode.displayName = 'FloatingActionNode';
